import { supabase } from "../lib/supabase";
import type { IntegrationConnectorId } from "./contracts";

export type GoogleWorkspaceProvider = "gmail" | "google_calendar";

type ConnectPayload = {
  provider: GoogleWorkspaceProvider;
  returnTo: string;
  userId?: string;
  accountId?: string;
};

type ConnectContext = {
  userId?: string;
  accountId?: string;
};

type ConnectResponse = {
  authorizationUrl?: string;
  connected?: boolean;
  reason?: string;
};

type SyncPayload = {
  provider: GoogleWorkspaceProvider;
  opportunityId: string;
  maxItems?: number;
};

type SyncSignalResponse = {
  signals: string[];
  syncedAt: string;
  source: "google_workspace" | "mock";
};

const CONNECT_FUNCTION = "google-workspace-connect";
const SYNC_FUNCTION = "google-workspace-sync";

function isGoogleProvider(id: IntegrationConnectorId): id is GoogleWorkspaceProvider {
  return id === "gmail" || id === "google_calendar";
}

function asErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unknown integration error";
}

export function buildMockSignals(provider: GoogleWorkspaceProvider): string[] {
  if (provider === "gmail") {
    return [
      "Subject: Interview Confirmation - Google\nHi, we'd like to invite you for an onsite interview...",
      "Subject: New Role: Principal Architect at Habasit America\nCheck out this JD: Principal Architect responsible for global operations...",
    ];
  }

  return [
    "Event: Onsite Interview @ Google (2026-04-10 10:00 AM)",
    "Event: Technical Screening @ Habasit (2026-04-12 02:00 PM)",
  ];
}

export async function requestGoogleWorkspaceConnect(
  id: IntegrationConnectorId,
  context?: ConnectContext,
): Promise<ConnectResponse> {
  if (!isGoogleProvider(id)) {
    return { connected: false, reason: `${id} is not a Google Workspace provider.` };
  }

  if (!supabase) {
    return {
      connected: false,
      reason: "Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
    };
  }

  const payload: ConnectPayload = {
    provider: id,
    returnTo: typeof window !== "undefined" ? window.location.href : "",
    userId: context?.userId,
    accountId: context?.accountId,
  };

  const { data, error } = await supabase.functions.invoke(CONNECT_FUNCTION, {
    body: payload,
  });

  if (error) {
    return { connected: false, reason: error.message };
  }

  const response = (data ?? {}) as ConnectResponse;
  if (!response.authorizationUrl && !response.connected) {
    return {
      connected: false,
      reason: response.reason ?? "Connect endpoint returned no authorization URL.",
    };
  }

  return response;
}

export async function syncGoogleWorkspaceSignals(
  id: IntegrationConnectorId,
  opportunityId: string,
  maxItems = 20,
): Promise<SyncSignalResponse> {
  if (!isGoogleProvider(id)) {
    return {
      signals: [],
      syncedAt: new Date().toISOString(),
      source: "mock",
    };
  }

  if (!supabase) {
    return {
      signals: buildMockSignals(id),
      syncedAt: new Date().toISOString(),
      source: "mock",
    };
  }

  const payload: SyncPayload = {
    provider: id,
    opportunityId,
    maxItems,
  };

  try {
    const { data, error } = await supabase.functions.invoke(SYNC_FUNCTION, {
      body: payload,
    });

    if (error) {
      return {
        signals: buildMockSignals(id),
        syncedAt: new Date().toISOString(),
        source: "mock",
      };
    }

    const response = (data ?? {}) as Partial<SyncSignalResponse>;
    if (!Array.isArray(response.signals)) {
      return {
        signals: buildMockSignals(id),
        syncedAt: new Date().toISOString(),
        source: "mock",
      };
    }

    return {
      signals: response.signals,
      syncedAt: response.syncedAt ?? new Date().toISOString(),
      source: response.source === "google_workspace" ? "google_workspace" : "mock",
    };
  } catch (error) {
    console.warn(`[Integrations] Falling back to mock sync: ${asErrorMessage(error)}`);
    return {
      signals: buildMockSignals(id),
      syncedAt: new Date().toISOString(),
      source: "mock",
    };
  }
}
